import React, { useEffect, useState } from 'react';
import { settingsApi, formatApiError } from '../services/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Sparkle, Info, EnvelopeSimple } from '@phosphor-icons/react';

const NUMERIC_KEY_MAP = {
  payment_reminder_overdue: 'days_overdue',
  license_expiry_warning: 'days_before',
  thank_you_after_return: 'days_after',
};
const NUMERIC_KEY_LABEL = {
  days_overdue: 'Tage überfällig',
  days_before: 'Tage vor Ablauf',
  days_after: 'Tage nach Rückgabe',
};

const AutoLetterRulesCard = () => {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await settingsApi.getAutoLetterRules();
      setRules(r.data.rules);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await settingsApi.updateAutoLetterRules(rules);
      setRules(r.data.rules);
      toast.success('Automatisierungs-Regeln gespeichert');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!rules) return null;

  return (
    <div className="card-swiss p-6" data-testid="auto-letter-rules-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#E60000]/10 rounded-sm flex items-center justify-center">
            <Sparkle weight="fill" size={20} className="text-[#E60000]" />
          </div>
          <div>
            <h2 className="text-lg font-bold">Automatisierungs-Regeln (Auto-Briefe)</h2>
            <p className="text-sm text-[#525866]">Automatische Brief-Entwürfe bei bestimmten Ereignissen</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="btn-primary" data-testid="save-auto-rules-btn">
          {saving ? 'Wird gespeichert…' : 'Speichern'}
        </Button>
      </div>

      <div className="p-3 mb-4 bg-[#F0F7FF] border border-[#B7DCFF] rounded-sm text-xs text-[#0066CC] flex items-start gap-2">
        <Info size={16} weight="bold" className="mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold mb-1">Auto-Briefe werden <u>immer nur als Entwurf</u> erstellt.</p>
          <p>Der E-Mail-Versand muss von dir bei jedem einzelnen Brief manuell bestätigt werden. Kein Brief wird automatisch verschickt.</p>
        </div>
      </div>

      <div className="space-y-3">
        {Object.entries(rules).map(([key, rule]) => {
          const numKey = NUMERIC_KEY_MAP[key];
          return (
            <div
              key={key}
              className={`border rounded-sm p-4 transition-colors ${
                rule.enabled ? 'border-[#E60000]/40 bg-[#FFF5F5]' : 'border-[#E5E7EB] bg-white'
              }`}
              data-testid={`rule-${key}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-[#111418]">{rule.label}</p>
                    <span className="text-xs px-2 py-0.5 bg-[#F7F8FA] text-[#525866] rounded-sm">
                      {rule.trigger === 'on_checkout' ? 'bei Rückgabe' : 'Tägliche Prüfung'}
                    </span>
                  </div>
                  <p className="text-xs text-[#525866] mt-1">{rule.description}</p>
                </div>
                <Switch
                  checked={rule.enabled}
                  onCheckedChange={(v) => setRules({ ...rules, [key]: { ...rule, enabled: v } })}
                  data-testid={`rule-${key}-toggle`}
                />
              </div>
              {numKey && rule.enabled && (
                <div className="mt-3 pt-3 border-t border-[#E5E7EB] flex items-center gap-3">
                  <Label className="text-xs">{NUMERIC_KEY_LABEL[numKey]}:</Label>
                  <Input
                    type="number"
                    min="1"
                    max="365"
                    value={rule[numKey] ?? ''}
                    onChange={(e) => setRules({ ...rules, [key]: { ...rule, [numKey]: parseInt(e.target.value) || 0 } })}
                    className="w-24 font-mono"
                    data-testid={`rule-${key}-days`}
                  />
                  <span className="text-xs text-[#8A94A6]">Tage</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-3 border-t border-[#F0F1F3] text-xs text-[#8A94A6] flex items-center gap-1.5">
        <EnvelopeSimple size={12} weight="bold" />
        Alle Entwürfe erscheinen auf der Seite <span className="font-semibold text-[#525866]">Briefe</span> mit dem Tag &bdquo;auto&ldquo;.
      </div>
    </div>
  );
};

export default AutoLetterRulesCard;
